import { memo, useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import type {
  AuditActorType,
  AuditEvent,
  AuditPrincipal,
  FieldChange,
  ResourceRef,
} from '@/types/audit';
import {
  defaultTitle,
  eventOverrides,
  formatFieldValue,
  type TFn,
} from './eventRegistry';
import type { ResolveItemLabel } from './AuditTrailModal';
import { AuditMarkdownBody } from './AuditMarkdownBody';
import { FormSchemaDiff } from './FormSchemaDiff';
import styles from './AuditEventRow.module.scss';

type PreviewFormHandler = (args: {
  event: AuditEvent;
  change: FieldChange;
  side: 'before' | 'after';
}) => void;

interface AuditEventRowProps {
  event: AuditEvent;
  resolveItemLabel?: ResolveItemLabel;
  onPreviewForm?: PreviewFormHandler;
}

function AuditEventRowInner({ event, resolveItemLabel, onPreviewForm }: AuditEventRowProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(false);
  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);

  const override = eventOverrides[event.eventName];

  const title: string = useMemo(() => {
    if (override?.renderTitle) return override.renderTitle(event, t);
    return defaultTitle(event);
  }, [event, override, t]);

  const body: ReactNode = useMemo(() => {
    if (override?.renderBody) return override.renderBody(event, t);

    // Preserve leading whitespace — indent encodes list nesting; `.trim()`
    // would flatten 2-space sub-bullets to top level.
    const markdownLines = (event.renderedMarkdown ?? []).filter(
      (s) => s.trim().length > 0,
    );

    // When markdown is present we skip the per-field diff path, but we still
    // need to surface FORM-typed changes (their `<FormSchemaDiff>` carries the
    // Preview-form button). Render markdown summary + any FORM diffs alongside.
    if (markdownLines.length > 0) {
      const formChanges = (event.changesJson ?? []).filter(
        (c) => c.fieldType === 'FORM',
      );
      return (
        <>
          <AuditMarkdownBody lines={markdownLines} />
          {formChanges.map((change, idx) => (
            <FormSchemaDiff
              key={idx}
              before={change.beforeValue}
              after={change.afterValue}
              onPreview={(side: 'before' | 'after') =>
                onPreviewForm?.({ event, change, side })
              }
            />
          ))}
        </>
      );
    }

    return defaultBody(event, t, expanded, toggleExpanded, resolveItemLabel, onPreviewForm);
  }, [event, override, t, expanded, toggleExpanded, resolveItemLabel, onPreviewForm]);

  const { date, time } = useMemo(() => formatTimestampParts(event.timestamp), [event.timestamp]);
  const actor = useMemo(() => actorForEvent(event, t), [event, t]);

  return (
    <li className={styles.row}>
      <time className={styles.timestamp} dateTime={event.timestamp}>
        <div className={styles.date}>{date}</div>
        <div className={styles.time}>{time}</div>
      </time>
      <div className={styles.actor}>
        <div className={styles.actorAvatar} aria-hidden="true">
          {actor.initial}
        </div>
        <div className={styles.actorName}>{actor.name}</div>
      </div>
      <div className={styles.updates}>
        <div className={styles.title}>{title}</div>
        {body && <div className={styles.bodyText}>{body}</div>}
      </div>
    </li>
  );
}

// Memo so a 50+ row list doesn't re-walk every row on time-window / pagination
// changes. All props are stable refs (event from query, useCallback'd handlers).
export const AuditEventRow = memo(AuditEventRowInner);

/**
 * Resolve the actor display from the event. Prefers the read-time enriched
 * `event.actor.fullName` / `email` (populated by watcher's actor_enricher);
 * falls back to a localised placeholder per actor type when enrichment is
 * absent — keeps older audits and SYSTEM rows readable.
 */
function actorForEvent(event: AuditEvent, t: TFn): { name: string; initial: string } {
  const enrichedName = principalDisplayName(event.actor);
  if (enrichedName) {
    return { name: enrichedName, initial: enrichedName.trim().charAt(0).toUpperCase() || '·' };
  }
  return placeholderActor(event.actorType, t);
}

function principalDisplayName(p?: AuditPrincipal): string | null {
  if (!p) return null;
  return extractDtoLabel(p as Record<string, unknown>);
}

/**
 * Pull a human-readable label out of an enriched DTO inlined by the watcher.
 *
 * Source DTOs the watcher returns:
 * - **User** (`platform-service /internal/users/bulk`): `{ id, firstName, lastName, email, ... }`
 * - **Synthetic SYSTEM / EXTERNAL_USER actor** (watcher actor_enricher): `{ id, type, name, email }`
 * - **Role / Permission / Country / etc.** (per resource bulk endpoint): typically `{ id, name, ... }`
 *
 * Lookup order is widest-first so newer/forward-compat shapes win, then User
 * (firstName+lastName composite), then short-name catalogs, then identifier
 * fallbacks. The `??` chain short-circuits on the first non-null candidate.
 */
function extractDtoLabel(obj: Record<string, unknown>): string | null {
  return (
    strField(obj.fullName) ??
    composeName(strField(obj.firstName), strField(obj.lastName)) ??
    strField(obj.name) ??
    strField(obj.label) ??
    strField(obj.code) ??
    strField(obj.title) ??
    strField(obj.email)
  );
}

function composeName(first: string | null, last: string | null): string | null {
  if (!first && !last) return null;
  return [first, last].filter(Boolean).join(' ');
}

function strField(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

function placeholderActor(
  actorType: AuditActorType,
  t: TFn,
): { name: string; initial: string } {
  switch (actorType) {
    case 'SYSTEM':
      return { name: t('K_AUDIT_ACTOR_SYSTEM', 'System'), initial: 'S' };
    case 'EXTERNAL_USER':
      return { name: t('K_AUDIT_ACTOR_EXTERNAL', 'External user'), initial: 'E' };
    case 'USER':
    default:
      return { name: t('K_AUDIT_ACTOR_USER', 'User'), initial: 'U' };
  }
}

function renderChangeBlock(
  change: FieldChange,
  t: TFn,
  resolveItemLabel?: ResolveItemLabel,
  onPreviewForm?: PreviewFormHandler,
  event?: AuditEvent,
): ReactNode {
  const fieldLabel = change.localizationKey
    ? t(change.localizationKey, change.fieldName)
    : change.fieldName;

  if (change.fieldType === 'FORM') {
    return (
      <FormSchemaDiff
        before={change.beforeValue}
        after={change.afterValue}
        onPreview={(side: 'before' | 'after') =>
          event && onPreviewForm?.({ event, change, side })
        }
      />
    );
  }

  // REFERENCE — values are foreign-key UUIDs (or arrays of UUIDs). Resolve
  // through the catalog-aware resolver before display. Falls through to the
  // generic array-diff renderer when before/after is array-shaped so set
  // semantics (Added / Removed) carry over.
  if (change.fieldType === 'REFERENCE') {
    const beforeIsArray = Array.isArray(change.beforeValue);
    const afterIsArray = Array.isArray(change.afterValue);
    if (beforeIsArray || afterIsArray) {
      const beforeArr = beforeIsArray ? (change.beforeValue as unknown[]) : [];
      const afterArr = afterIsArray ? (change.afterValue as unknown[]) : [];
      return renderArrayDiff(fieldLabel, beforeArr, afterArr, t, resolveItemLabel, change.referenceType);
    }
    return renderReferenceScalar(fieldLabel, change, t, resolveItemLabel);
  }

  // For JSON-typed array fields (e.g. role permissions on legacy events that
  // pre-date REFERENCE), render a structural set-diff (Added / Removed)
  // rather than dumping comma-joined UUIDs as before → after. Forward
  // `referenceType` if the producer opportunistically tagged it during the
  // BE migration window — the resolver can then dispatch correctly even
  // before fieldType flips to REFERENCE.
  if (
    change.fieldType === 'JSON' &&
    (Array.isArray(change.beforeValue) || Array.isArray(change.afterValue))
  ) {
    const beforeArr = Array.isArray(change.beforeValue) ? change.beforeValue : [];
    const afterArr = Array.isArray(change.afterValue) ? change.afterValue : [];
    return renderArrayDiff(fieldLabel, beforeArr, afterArr, t, resolveItemLabel, change.referenceType);
  }

  const before = formatFieldValue(change.beforeValue, change.fieldType, t);
  const after = formatFieldValue(change.afterValue, change.fieldType, t);
  return (
    <>
      <div className={styles.fieldLabel}>{fieldLabel}</div>
      <div className={styles.fieldValues}>
        <span>{before}</span>
        <span className={styles.arrow} aria-hidden="true">{' → '}</span>
        <span>{after}</span>
      </div>
    </>
  );
}

function renderReferenceScalar(
  label: string,
  change: FieldChange,
  t: TFn,
  resolveItemLabel?: ResolveItemLabel,
): ReactNode {
  const before = referenceLabel(change.beforeValue, change.referenceType, resolveItemLabel, t);
  const after = referenceLabel(change.afterValue, change.referenceType, resolveItemLabel, t);
  return (
    <>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValues}>
        <span>{before}</span>
        <span className={styles.arrow} aria-hidden="true">{' → '}</span>
        <span>{after}</span>
      </div>
    </>
  );
}

function referenceLabel(
  value: unknown,
  referenceType: string | undefined,
  resolveItemLabel: ResolveItemLabel | undefined,
  t: TFn,
): string {
  if (value === null || value === undefined || value === '') {
    return t('K_AUDIT_VALUE_EMPTY', '—');
  }
  // Watcher inlines the resolved DTO directly into beforeValue/afterValue for
  // REFERENCE fields (single value → object, array → array of objects). Prefer
  // the inlined DTO over the host-supplied resolver — it's already the truth.
  if (typeof value === 'object') {
    const dtoLabel = extractDtoLabel(value as Record<string, unknown>);
    if (dtoLabel) return dtoLabel;
    // Fall through to resolver if the object had no recognisable label field.
    const id = strField((value as Record<string, unknown>).id);
    if (id && resolveItemLabel) {
      const resolved = resolveItemLabel(id, referenceType);
      if (resolved !== null && resolved !== undefined) return resolved;
    }
    if (id) return displayKey(id);
  }
  const key = String(value);
  if (resolveItemLabel) {
    const resolved = resolveItemLabel(key, referenceType);
    if (resolved !== null && resolved !== undefined) return resolved;
  }
  return displayKey(key);
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Walk both before/after arrays and collect every inlined DTO into an
 * `id → displayLabel` map. Skipped for primitive items (strings, numbers etc.)
 * which carry no inlined data to extract.
 */
function collectInlinedLabels(before: unknown[], after: unknown[]): Map<string, string> {
  const out = new Map<string, string>();
  const visit = (items: unknown[]) => {
    for (const item of items) {
      if (item === null || item === undefined || typeof item !== 'object') continue;
      const obj = item as Record<string, unknown>;
      const id = strField(obj.id);
      if (!id) continue;
      const label = extractDtoLabel(obj);
      if (label) out.set(id, label);
    }
  };
  visit(before);
  visit(after);
  return out;
}

function stableKey(item: unknown): string | null {
  if (item === null || item === undefined) return null;
  if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
    return String(item);
  }
  if (typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    if (typeof obj.id === 'string' && obj.id) return obj.id;
    if (typeof obj.code === 'string' && obj.code) return obj.code;
    if (typeof obj.name === 'string' && obj.name) return obj.name;
    if (typeof obj.label === 'string' && obj.label) return obj.label;
  }
  return null;
}

function displayKey(key: string): string {
  // Truncate UUID-shaped strings so a long list stays readable.
  if (UUID_REGEX.test(key)) return `${key.slice(0, 8)}…`;
  return key;
}

function renderArrayDiff(
  label: string,
  before: unknown[],
  after: unknown[],
  t: TFn,
  resolveItemLabel?: ResolveItemLabel,
  referenceType?: string,
): ReactNode {
  const beforeKeys = before.map(stableKey).filter((k): k is string => k !== null);
  const afterKeys = after.map(stableKey).filter((k): k is string => k !== null);
  const beforeSet = new Set(beforeKeys);
  const afterSet = new Set(afterKeys);
  const added = afterKeys.filter((k) => !beforeSet.has(k));
  const removed = beforeKeys.filter((k) => !afterSet.has(k));

  // Build id → display-label from inlined DTOs the watcher returns alongside
  // (or in place of) bare UUIDs. The set-diff above keys on `id` to retain
  // identity semantics; this map lets the rendered label come from the inlined
  // DTO without a second round-trip through the host resolver.
  const inlinedLabels = collectInlinedLabels(before, after);

  // Display priority:
  //   1. inlined DTO label (already correct, no async needed)
  //   2. host-supplied resolver (legacy or supplementary catalogs)
  //   3. truncated UUID
  const display = (key: string): string => {
    const inlined = inlinedLabels.get(key);
    if (inlined) return inlined;
    if (resolveItemLabel) {
      const resolved = resolveItemLabel(key, referenceType);
      if (resolved !== null && resolved !== undefined) return resolved;
    }
    return displayKey(key);
  };

  return (
    <>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValues}>
        {added.length === 0 && removed.length === 0 ? (
          <span className={styles.diffUnchanged}>
            {t('K_AUDIT_DIFF_UNCHANGED', 'No items changed')}
          </span>
        ) : (
          <>
            {added.length > 0 && (
              <div className={styles.added}>
                {`+ ${t('K_AUDIT_ADDED', 'Added')} (${added.length}): ${added
                  .map(display)
                  .join(', ')}`}
              </div>
            )}
            {removed.length > 0 && (
              <div className={styles.removed}>
                {`− ${t('K_AUDIT_REMOVED', 'Removed')} (${removed.length}): ${removed
                  .map(display)
                  .join(', ')}`}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function defaultBody(
  event: AuditEvent,
  t: TFn,
  expanded: boolean,
  onToggle: () => void,
  resolveItemLabel?: ResolveItemLabel,
  onPreviewForm?: PreviewFormHandler,
): ReactNode {
  const changes = event.changesJson ?? [];
  if (changes.length === 0) return null;

  // When relatedResources is set, group changes by entityRef so the row
  // surfaces "this part of the change touched <related-entity>". Changes
  // without an entityRef belong to the primary resource and render first.
  // Render through the grouped path whenever any change carries a non-primary
  // entityRef — even with one group — so the related-entity heading appears
  // for the common "single change on one related resource" event shape.
  const grouped = groupByEntityRef(changes, event);
  const hasNonPrimaryGroup = grouped.some((g) => g.ref !== null);
  if (grouped.length > 1 || hasNonPrimaryGroup) {
    return renderGroupedChanges(grouped, t, resolveItemLabel, onPreviewForm, event);
  }

  const first = changes[0];
  if (!first) return null;

  // Single change — no toggle needed
  if (changes.length === 1) {
    return renderChangeBlock(first, t, resolveItemLabel, onPreviewForm, event);
  }

  if (expanded) {
    return (
      <>
        {changes.map((change, idx) => (
          <div key={idx} className={styles.changeLine}>
            {renderChangeBlock(change, t, resolveItemLabel, onPreviewForm, event)}
          </div>
        ))}
        <button type="button" className={styles.toggle} onClick={onToggle}>
          {t('K_AUDIT_SHOW_LESS', 'Show less')}
        </button>
      </>
    );
  }

  // Collapsed — first change as block + toggle on its own line below
  return (
    <>
      {renderChangeBlock(first, t, resolveItemLabel, onPreviewForm, event)}
      <button type="button" className={styles.toggle} onClick={onToggle}>
        {t('K_AUDIT_MORE_CHANGES', '+{n} more').replace('{n}', String(changes.length - 1))}
      </button>
    </>
  );
}

interface ChangeGroup {
  ref: ResourceRef | null;
  changes: FieldChange[];
}

function groupByEntityRef(changes: FieldChange[], event: AuditEvent): ChangeGroup[] {
  const hasRelated = (event.relatedResources?.length ?? 0) > 0;
  if (!hasRelated) return [{ ref: null, changes }];

  const groups = new Map<string, ChangeGroup>();
  const order: string[] = [];
  for (const c of changes) {
    const key = c.entityRef ? `${c.entityRef.resourceType}:${c.entityRef.resourceId}` : '__primary__';
    let group = groups.get(key);
    if (!group) {
      group = { ref: c.entityRef ?? null, changes: [] };
      groups.set(key, group);
      order.push(key);
    }
    group.changes.push(c);
  }
  return order.map((k) => groups.get(k)!);
}

function renderGroupedChanges(
  groups: ChangeGroup[],
  t: TFn,
  resolveItemLabel?: ResolveItemLabel,
  onPreviewForm?: PreviewFormHandler,
  event?: AuditEvent,
): ReactNode {
  return (
    <>
      {groups.map((group, idx) => {
        const heading =
          group.ref === null
            ? null
            : groupHeading(group.ref, t, resolveItemLabel);
        return (
          <div key={idx} className={styles.entityGroup}>
            {heading && <div className={styles.entityGroupHeading}>{heading}</div>}
            {group.changes.map((change, j) => (
              <div key={j} className={styles.changeLine}>
                {renderChangeBlock(change, t, resolveItemLabel, onPreviewForm, event)}
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}

function groupHeading(
  ref: ResourceRef,
  t: TFn,
  resolveItemLabel?: ResolveItemLabel,
): string {
  const resolved = resolveItemLabel?.(ref.resourceId, ref.resourceType);
  const resolvedLabel = resolved && resolved.trim().length > 0 ? resolved : displayKey(ref.resourceId);
  // "Role: Admin" / "Permission: <uuid>"
  const typeLabel = humanizeResourceType(ref.resourceType, t);
  return `${typeLabel}: ${resolvedLabel}`;
}

function humanizeResourceType(resourceType: string, t: TFn): string {
  // Allow locale to win when seeded (K_AUDIT_RESOURCE_<TYPE>); otherwise
  // humanise the canonical wire string.
  const key = `K_AUDIT_RESOURCE_${resourceType}`;
  const fallback = resourceType
    .toLowerCase()
    .split('_')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');
  return t(key, fallback);
}

/**
 * Split the timestamp into a date line ("Oct 25, 2021") and a time line
 * ("12:18 PM") for the timestamp column.
 */
function formatTimestampParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: iso, time: '' };
  const date = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return { date, time };
}
